import React, { useCallback, useEffect, useState } from 'react';
import LYWOLogoSmall from "../assets/images/LYWOLogoSmall.svg";
import {
  User, Briefcase, Calendar, IndianRupee, Phone, Mail,
  Brain, CheckSquare, Award, Star, MessageSquare, X, MapPin,
  GraduationCap, ClipboardList, CreditCard, Target, ShieldAlert,
  MoreVertical, FileText, Trash2, ThumbsDown, Linkedin, Link as LinkIcon, LogOut, List
} from 'lucide-react';
import { useContext } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { AppContext } from '../context/AppContext';
import { simpleDeleteCall, simpleGetCallWithErrorResponse } from '../Api/ApiServices';
import { ApiConfig } from '../Api/ApiConfig';
import { formatDate } from '../common';
import { PostCallWithErrorResponse } from '../Api/ApiServices';
import { toastMsg } from '../component/toast';
import UserData from './CandidatDetails';
import PersonalityDetails from '../component/personalityDeatils';
import IconDisplay from '../component/getIcon';

/**
 * =================================================================================
 * 1. CONFIGURATION & MOCK DATA
 * In a real app, this would come from an API or Prop injection.
 * =================================================================================
 */


const DEFAULT_DATA = {
  candidate: {
    name: "Mamtha",
    role: "Engineering Manager",
    applicationFor: "Engineering Manager (Rapsri Engineering Products Ltd)",
    contact: {
      phone: "8296222980",
      email: "mamthav757@gmail.com"
    },
    details: {
      experience: "9 Years",
      location: "Bangalore",
      salary: "9 - 12 Lakhs",
      availability: "30 Aug 2025",
      buyOut: "No",
      techAssessment: "4/17"
    },
    education: {
      degree: "BE in Information Science and Engineering",
      institution: "VTU University, Bangalore",
      performance: "N/A",
      dates: "2012 – 2016"
    }
  },
  skills: [
    "Team Handling", "Leadership", "Decision Making",
    "Time Management", "Client Acquisition", "Communication",
    "Strategic Thinking", "Problem Solving", "ERP Tools"
  ],
  questionnaire: [
    { q: "Hands-on tool/die & CAD?", a: "No" },
    { q: "Led cross-functional teams?", a: "Yes" },
    { q: "5+ years manufacturing?", a: "No" }
  ],
  notes: [
    {
      author: "Arun V.",
      text: "Candidate lacks core manufacturing experience required for this role.",
      time: "12 Dec 2024, 10:30 AM",
      tags: ["Not Relevant", "Skill Gap"]
    },
    {
      author: "Sarah J.",
      text: "Strong personality fit as Motivator/Influencer, but technical alignment is weak.",
      time: "13 Dec 2024, 02:15 PM",
      tags: ["Interesting", "Culture Fit"]
    },
    {
      author: "Mike R.",
      text: "Notice period is quite long (Aug 2025). Need to check if negotiable.",
      time: "14 Dec 2024, 09:00 AM",
      tags: ["Hold", "Notice Period"]
    },
    {
      author: "Priya K.",
      text: "Good grasp of ERP systems, but could not answer questions on casting defects.",
      time: "15 Dec 2024, 11:45 AM",
      tags: ["In Progress", "Technical Gap"]
    },
    {
      author: "Vikram S.",
      text: "Might be a better fit for the Sales Lead role given her acquisition background.",
      time: "16 Dec 2024, 04:20 PM",
      tags: ["Shortlist", "Internal Transfer"]
    }
  ],
  behavioral: {
    benchmark: {
      title: "Target Personalities",
      desc: "The organization requires a leader who can drive innovation ('Achiever'), mentor the team ('Motivator'), and influence stakeholders ('Persuader')."
    },
    profile: {
      primary: "Motivator",
      secondary: "Influencer",
      brief: "Mamtha is primarily a Motivator (dependable, social, assertive), supported by the goal-oriented traits of an Achiever, and secondarily influenced by the confident nature of an Influencer. She presents a unique blend of competitive drive and a pleasant demeanor."
    },
    table: [
      {
        target: "ACHIEVER",
        p: "Reliable and deliberate. Focuses on controlling circumstances to ensure goals are met without rushing.",
        s: "Becomes stubborn ('obstinate'). She will work hard to defend her position but may resist necessary compromises.",
        bridging: "Highly competitive and calculating. Strives for excellence and carefully weighs risks to ensure she wins."
      },
      {
        target: "MOTIVATOR",
        p: "Warm, patient, and a natural facilitator. She builds good relations and keeps the team atmosphere supportive.",
        s: "High energy and charisma. She becomes the 'center of attention' and inspires others through enthusiasm.",
        bridging: "Leads by example through confidence. She is 'self-promoting,' showing others what success looks like."
      },
      {
        target: "PERSUADER",
        p: "Social and dependable. She confidently suggests new ideas and uses her skills to assign tasks effectively.",
        s: "A natural 'Influencer.' She uses intuition and charm to network, connect easily, and win people over.",
        bridging: "Challenges the status quo. She has the courage ('defiance') to stand up for her beliefs and advocate for herself."
      }
    ]
  },
  highlights: {
    rating: "Low Match",
    points: [
      { t: "Team Management", d: "Managed 15 Retail Associates and 5 Retail Managers at Girnarsoft." },
      { t: "Revenue Growth", d: "Improved revenue significantly from 2.8 Cr/month to 36 Cr/month." },
      { t: "Operational Excellence", d: "Boosted conversion rates and cut losses; managed end-to-end retail operations." },
      { t: "Collaboration", d: "Cross-functional collaboration with finance, sales, and marketing teams." },
      { t: "Achievement", d: "Awarded 'Star of the Quarter' by CEO at Girnarsoft Automobiles." }
    ]
  },
  criticalAreas: [
    { t: "Educational Mismatch", d: "Degree in Computer Engineering vs required Mechanical Engineering." },
    { t: "Industry Mismatch", d: "Experience is in Auto Sales/Tech (FMCG-Other) vs required Manufacturing/Casting." },
    { t: "Skills Gap", d: "Lacks hands-on tool/die design and CAD software experience." }
  ],
  workHistory: [
    { r: "Team Lead / Asst Manager", c: "Girnarsoft Automobiles", d: "2019 - 2024", f: "Client acquisition, Team management, Revenue growth." },
    { r: "Operations Manager", c: "Cars24 Services", d: "2017 - 2019", f: "Retail operations, Logistics, Process improvement." },
    { r: "SQL Developer", c: "NHS Pvt Ltd", d: "2016 - 2017", f: "Database optimization, ETL pipelines." }
  ]
};


const MODAL_CONTENT = {
  "Motivator": {
    description: "Motivators are dependable, social, assertive, and determined individuals. They have a clear idea of their life goals and the willpower to achieve them. They are effective facilitators who thrive on working with people and challenging tasks.",
    strengths: "Dependable, Socially skilled, Patient, Warm, Driven to achieve, Self-reliant.",
    challenges: "Being asked to look into too much detail; Routine and regimen.",
    motivations: "New opportunities; Strong need to achieve."
  },
  "Influencer": {
    description: "Influencers are confident, outgoing, and friendly individuals who love being the center of attention. They seek out social situations and are adept at interacting positively in almost any situation.",
    strengths: "Fun, Persuasive, Ideas-oriented, Creative, Original, Networkers.",
    challenges: "Needs constant approval; Pressured conditions.",
    motivations: "Being surrounded by people; Supportive and collaborative environment."
  },
  "Target Personalities": {
    description: "The ideal candidate for this role embodies three key traits: Achiever, Motivator, and Persuader. They must have the drive to execute high-quality work, the social intelligence to rally a team, and the diplomatic skills to influence stakeholders.",
    strengths: "Balanced leadership, high emotional intelligence, strategic execution",
    challenges: "Balancing the need for speed with the need for consensus",
    motivations: "Team success, organizational growth, influence"
  }
};


/**
 * =================================================================================
 * 2. STYLES (Self-contained for Portability)
 * =================================================================================
 */
const CSS_STYLES = `
:root {
  --lywo-primary: #0d6efd;
  --lywo-bg: #f5f8ff;
  --lywo-text: #212529;
  --lywo-font-base: 14px;
  --lywo-info-bg: #eef2ff;
  --lywo-success-bg: #d1e7dd; --lywo-success-text: #198754;
  --lywo-warning-bg: #fff3cd; --lywo-warning-text: #856404;
  --lywo-danger-bg: #f8d7da; --lywo-danger-text: #dc3545;
  --lywo-border-light: #e2e8f0;
  --lywo-radius: 0.375rem;
}


body { font-family: 'Inter', sans-serif !important; font-size: var(--lywo-font-base); color: var(--lywo-text); background-color: #f0f2f5; margin: 0; }
.mainWrap { background-color: var(--lywo-bg); min-height: 100vh; padding-bottom: 2rem; }


/* Header */
.app-header { background: #fff; border-bottom: 1px solid var(--lywo-border-light); padding: 0.5rem 1rem; display: flex; justify-content: space-between; align-items: center; box-shadow: 0 1px 2px rgba(0,0,0,0.05); margin-bottom: 1rem; position: sticky; top: 0; z-index: 100; }
.header-main { flex: 1; }
.header-title { font-size: 1.4rem; font-weight: 700; color: #000000; margin: 0; line-height: 1.1; }
.header-company { font-size: 0.9rem; color: #475569; font-weight: 500; margin: 0; line-height: 1.2; }
.header-role { font-size: 0.8rem; color: #64748b; margin: 0; line-height: 1.2; }
.header-right { text-align: right; }
.contact-bar { display: flex; gap: 1rem; font-size: 0.8rem; color: #475569; flex-wrap: wrap; }
.contact-item { display: flex; align-items: center; gap: 0.4rem; font-weight: 500; }
.contact-item svg { color: var(--lywo-primary); opacity: 0.8; }


/* Common UI Atoms */
.card { background: #fff; border-radius: var(--lywo-radius); border: 1px solid var(--lywo-border-light); padding: 1rem;box-shadow: 0 0.125rem 0.25rem rgba(0,0,0,0.05); height: 100%; }
.section-title { font-size: 0.75rem; font-weight: 700; color: #64748b; text-transform: uppercase; margin-bottom: 0.75rem; display: flex; align-items: center; gap: 0.4rem; letter-spacing: 0.5px; }


.icon-box { width: 30px; height: 30px; border-radius: 6px; display: flex; align-items: center; justify-content: center; flex-shrink: 0; }
.icon-box.blue { background: var(--lywo-info-bg); color: var(--lywo-primary); }
.icon-box.green { background: var(--lywo-success-bg); color: var(--lywo-success-text); }
.icon-box.purple { background: #faf5ff; color: #9333ea; }
.icon-box.gray { background: #f1f5f9; color: #64748b; }


.badge { display: inline-block; padding: 0.25rem 0.5rem; border-radius: 3px; font-size: 0.7rem; font-weight: 700; text-transform: uppercase; }
.badge.good { background: var(--lywo-success-bg); color: var(--lywo-success-text); }
.badge.bad { background: var(--lywo-danger-bg); color: var(--lywo-danger-text); }
.badge.match { background: #f97316; color: white; padding: 0.2rem 0.5rem; }


/* Profile Section */
.profile-row { display: flex; align-items: center; gap: 0.6rem; margin-bottom: 0.5rem; }
.profile-label { font-size: 0.7rem; color: #64748b; margin: 0; font-weight: 600; text-transform: uppercase; }
.profile-value { font-weight: 600; font-size: 0.9rem; color: var(--lywo-text); margin: 0; }
.edu-box { background: #f8fafc; padding: 0.75rem; border-radius: var(--lywo-radius); border: 1px solid var(--lywo-border-light); margin-top: 0.5rem; width: 100%; }
.edu-row { display: flex; justify-content: space-between; font-size: 0.75rem; margin-top: 0.25rem; }


/* Questionnaire */
.list-item { border-bottom: 1px solid var(--lywo-border-light); padding: 0.5rem 0; display: flex; justify-content: space-between; align-items: center; font-size: 0.85rem; }
.list-item:last-child { border: none; padding-bottom: 0; }


/* Notes & Tags */
.note-card { background: #f8fafc; padding: 0.75rem; border-radius: var(--lywo-radius); border: 1px solid var(--lywo-border-light); margin-bottom: 0.75rem; }
.note-header { display: flex; justify-content: space-between; margin-bottom: 0.25rem; font-size: 0.75rem; }
.note-tags-row { display: flex; flex-wrap: wrap; gap: 0.35rem; margin-bottom: 0.5rem; }
.note-tag { padding: 1px 6px; border-radius: 3px; font-size: 0.7rem; font-family: monospace; font-weight: 600; border: 1px solid transparent; }


/* Skills */
.skill-tag { display: inline-block; padding: 0.2rem 0.6rem; background: #f8f9fa; color: var(--lywo-text); border-radius: 4px; font-size: 0.75rem; font-weight: 500; margin: 0 0.25rem 0.25rem 0; border: 1px solid var(--lywo-border-light); }


/* Behavioral */
.personality-section { border-top: 3px solid var(--lywo-primary); }
.beh-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 1rem; background: var(--lywo-info-bg); padding: 1rem; border-radius: var(--lywo-radius); margin-bottom: 1rem; }
.link-btn { background: none; border: none; padding: 0; color: var(--lywo-primary); font-weight: 700; font-size: 1rem; border-bottom: 1px dashed currentColor; cursor: pointer; }
.beh-table { width: 100%; border-collapse: collapse; font-size: 0.8rem; border: 1px solid var(--lywo-border-light); margin-top: 0.75rem; }
.beh-table th { text-align: left; padding: 0.5rem; background: #f8fafc; color: #64748b; font-weight: 700; border-bottom: 2px solid #cbd5e1; text-transform: uppercase; font-size: 0.65rem; }
.beh-table td { padding: 0.5rem; border-bottom: 1px solid var(--lywo-border-light); vertical-align: top; }


/* Highlights & Alerts */
.card-accent-left { border-left: 4px solid #f97316; }
.highlight-item { display: flex; gap: 0.5rem; margin-bottom: 0.5rem; font-size: 0.85rem; }
.card-alert { background-color: var(--lywo-danger-bg); border: 1px solid #f5c6cb; }
.alert-item { background: rgba(255,255,255,0.8); padding: 0.5rem; border-radius: 4px; margin-bottom: 0.5rem; font-size: 0.85rem; color: #842029; border: 1px solid #f5c6cb; }


/* Work History */
.work-grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(280px, 1fr)); gap: 0.75rem; }
.work-item { background: #fff; border: 1px solid var(--lywo-border-light); padding: 1rem; border-radius: 4px; display: flex; flex-direction: column; box-shadow: 0 1px 2px rgba(0,0,0,0.02); }
.work-focus { margin-top: auto; background: #f8f9fa; padding: 0.5rem; border-radius: 4px; border: 1px solid var(--lywo-border-light); font-size: 0.75rem; }


/* Modal & Context Menu */

.context-menu-container { position: relative; display: inline-block; margin-right: 0.5rem; }
.context-menu-btn { background: none; border: none; padding: 4px; border-radius: 4px; cursor: pointer; color: #64748b; transition: background 0.2s; display: flex; align-items: center; }
.context-menu-btn:hover { background-color: #f1f5f9; color: var(--lywo-primary); }
.context-menu-dropdown { position: absolute; top: 100%; right: 0 !important; margin-top: 4px; background: white; border: 1px solid var(--lywo-border-light); border-radius: var(--lywo-radius); box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06); min-width: 180px; z-index: 1000; overflow: hidden; }
.context-menu-item { display: flex; align-items: center; gap: 0.5rem; width: 100%; padding: 0.6rem 1rem; border: none; background: none; text-align: left; font-size: 0.85rem; color: var(--lywo-text); cursor: pointer; transition: background 0.1s; }
.context-menu-item:hover { background-color: #f8fafc; }
.context-menu-item.danger { color: var(--lywo-danger-text); }
.context-menu-item.danger:hover { background-color: #fff5f5; }


@media print {
  .btn-print, .modal-overlay, .context-menu-container { display: none; }
  .header { position: static; border-bottom: 2px solid #000; }
  .col-left { width: 32%; float: left; padding-right: 15px; }
  .col-right { width: 66%; float: right; }
  .card { break-inside: avoid; border: 1px solid #ccc; box-shadow: none; margin-bottom: 1rem; }
  .header-right { text-align: right; }
}
`;


/**
 * =================================================================================
 * 3. HELPER FUNCTIONS & UI ATOMS
 * =================================================================================
 */


// Colors for tags
const getTagStyle = (tag) => {
  const lower = tag.toLowerCase();
  if (['reject', 'not relevant', 'technical gap', 'skill gap'].some(t => lower.includes(t))) {
    return { background: '#fef2f2', color: '#b91c1c', borderColor: '#fecaca' };
  }
  if (['hire', 'select', 'shortlist', 'call to interview', 'interesting', 'culture fit', 'internal transfer'].some(t => lower.includes(t))) {
    return { background: '#f0fdf4', color: '#15803d', borderColor: '#bbf7d0' };
  }
  if (['hold', 'in progress', 'notice period'].some(t => lower.includes(t))) {
    return { background: '#fffbeb', color: '#b45309', borderColor: '#fde68a' };
  }
  return { background: '#f8fafc', color: '#475569', borderColor: '#e2e8f0' };
};


const InfoItem = ({ icon: Icon, color, label, value }) => (
  <div className="profile-row">
    <div className={`icon-box ${color}`}><Icon size={16} /></div>
    <div>
      <p className="profile-label">{label}</p>
      <div className="profile-value">{value}</div>
    </div>
  </div>
);


/**
 * =================================================================================
 * 4. SUB-COMPONENTS
 * =================================================================================
 */


const Header = ({ candidate, code, userId, adminData }) => {
  const [showMenu, setShowMenu] = useState(false);

  const { handleLogout } = useContext(AppContext);

  const navigate = useNavigate()


  const markNotIntrested = () => {

    let payload = {
      "code": code,
      "tag": "Not Interested",
      "isAdd": true,
      "userIds": [userId],
      admin: adminData?.email
    }

    PostCallWithErrorResponse(ApiConfig.UPDATE_TAGS, payload).then((res) => {

      if (res?.json?.succeded) {
        toastMsg("success", "Candidate marked as Not Interested");
      } else {
        toastMsg("error", res?.json?.response?.statusMessage || "Something went wrong");
      }

    })

  }


  const deleteCandidate = () => {
    const url = `${ApiConfig.DELETE_USER_AND_DEP}userid=${userId}`;

    simpleDeleteCall(url, userId)
      .then((res) => {
        if (res?.response?.status) {

          toastMsg("success", "User record deleted successfully.");

          navigate(-1)

        } else {
          console.log("Unexpected response");
        }
      })

  }


  return (
    <header className="app-header">
      <div className="header-main">

        <img src={LYWOLogoSmall} alt="" />
      </div>

      <div className="header-right">

        <div style={{
          display: "flex",
          justifyContent: "flex-end", // 👈 moves content to end
          alignItems: "center",
          marginBottom: "0.25rem",
        }}>

          <h1 className="header-title">{candidate?.candidateName}</h1>
        </div>

        <div className="contact-bar">
          <div className="contact-item"><Phone size={14} /> {candidate?.mobile}</div>
          <div className="contact-item"><Mail size={14} /> {candidate?.email}</div>

        </div>

      </div>

      <div className="context-menu-container">
        <button className="context-menu-btn" onClick={() => setShowMenu(!showMenu)}>
          <MoreVertical size={20} />
        </button>
        {showMenu && (
          <>
            <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: 999 }} onClick={() => setShowMenu(false)} />
            <div className="context-menu-dropdown">
              <button className="context-menu-item" onClick={() => setShowMenu(false)}>
                <FileText size={14} /> Download PDF
              </button>
              <button className="context-menu-item danger" onClick={() => {

                markNotIntrested()
                setShowMenu(false)
              }}>
                <ThumbsDown size={14} /> Mark Not Interested
              </button>
              <button className="context-menu-item danger" onClick={() => {
                deleteCandidate()
                setShowMenu(false)


              }}>
                <Trash2 size={14} /> Delete Candidate
              </button>
              <button className="context-menu-item" onClick={() => {
navigate(-1)

              }}>
                <List size={14} /> Candidate Listing
              </button>
              <button className="context-menu-item" onClick={() => {
                handleLogout()


              }}>
                <LogOut size={14} /> Logout
              </button>
            </div>
          </>
        )}
      </div>
    </header>
  );
};


const ProfileCard = ({ data }) => (
  <div className="card h-100">
    <div className="section-title"><User size={14} /> CANDIDATE PROFILE</div>
    <InfoItem icon={Briefcase} color="blue" label="Total Experience" value={data?.yearsOfExperience} />
    <InfoItem icon={MapPin} color="green" label="Location" value={data?.currentLocation} />
    <InfoItem icon={IndianRupee} color="green" label="Expected Salary" value={data?.expectedSalary} />
    <InfoItem icon={Calendar} color="purple" label="Availability" value={data?.availableBy?.split('T')[0]} />
    <InfoItem icon={CreditCard} color="gray" label="Buy Out" value={data?.buyOutOption ? "Yes" : "No"} />
    <InfoItem icon={ClipboardList} color="purple" label="Assessment" value={data?.technicalScore} />


    <div className="mt-3 pt-3 border-top">
      <p className="profile-label mb-2">ONLINE PRESENCE</p>
      <div className="d-flex flex-column gap-2">
{
 data?.resumeMatch&& data?.resumeMatch[0]?.onlinePresence?.map((i)=>{
    return <div className="d-flex align-items-center gap-2" style={{ fontSize: '0.85rem' }}>
  
    <IconDisplay type={i?.platform}/>
    <a href={i?.url} target="_blank" rel="noopener noreferrer" className="text-decoration-none text-truncate"
    // style={{ color: THEME.colors.primary }}
    >
      {i?.url}
    </a>
  </div>
  })
}
        

      </div>
    </div>

    <div style={{ marginTop: '1rem', paddingTop: '1rem', borderTop: '1px solid var(--lywo-border-light)' }}>
      <div className="d-flex gap-2 align-items-start">
        <div className="icon-box gray"><GraduationCap size={16} /></div>
        <div className="w-100">
          <p className="profile-label">EDUCATION</p>
          <div className="edu-box">
            <div className="fw-bold" style={{ fontSize: '0.85rem', marginBottom: '0.2rem' }}>{data?.education?.degree}</div>
            <div style={{ fontSize: '0.75rem', color: '#64748b', marginBottom: '0.4rem' }}>{data?.education?.institution}</div>
            <div className="edu-row">
              <span style={{ color: '#64748b' }}>{data?.education?.dates}</span>
              <span className="fw-semibold">{data?.education?.performance}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
);


const QuestionnaireCard = ({ data }) => (
  <div className="card">
    <div className="section-title"><CheckSquare size={14} /> QUESTIONNAIRE</div>
    {data?.customQuestions?.map((item, i) => (
      <div key={i} className="list-item">
        <span style={{ color: '#64748b', maxWidth: '85%' }}>{item?.questionText}</span>
        <span className={`badge ${data[`ans${i + 1}`] === 'Yes' ? 'good' : 'bad'}`}> {data[`ans${i + 1}`] || 'N/A'}</span>
      </div>
    ))}
  </div>
);


const NotesSection = ({ data }) => (
  <div className="card">
    <div className="section-title"><MessageSquare size={14} /> COLLABORATORS NOTES AND TAGS</div>
    {data?.adminNotes?.map((note, i) => (
      <div key={i} className="note-card">
        <div className="note-header">
          <strong>{note?.email}</strong>
          <span style={{ fontFamily: 'monospace', fontSize: '0.7rem', color: '#64748b' }}> {formatDate(note?.createdAt)}</span>
        </div>
        {data?.tags && data?.tags?.length > 0 && (
          <div className="note-tags-row">
            {data?.tags?.filter((tag) => {
              return tag?.email === note?.email
            })?.map(tag => (
              <span key={tag} className="note-tag" style={getTagStyle(tag?.name)}>{tag?.name}</span>
            ))}
          </div>
        )}
        <p style={{ fontSize: '0.8rem', margin: 0 }}>{note?.notes}</p>
      </div>
    ))}
  </div>
);


const SkillsSection = ({ data }) => (
  <div className="card">
    <div className="section-title"><Target size={14} /> KEY SKILLS</div>
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.25rem' }}>
      {data?.resumeMatch && data?.resumeMatch[0]?.resumeMatchSkills?.map((skill, i) => <span key={i} className="skill-tag">{skill?.name}</span>)}
    </div>
  </div>
);


const BehavioralAnalysis = ({ data, onLinkClick }) => (
  <div className="card personality-section">
    <div className="section-title" style={{ color: 'var(--lywo-primary)' }}><Brain size={14} /> Job Role and Candidate Behavior Mapping Summary</div>


    <div className="beh-grid">
      <div>
        <h4 style={{ fontSize: '0.7rem', fontWeight: 700, color: 'var(--lywo-primary)', textTransform: 'uppercase', marginBottom: '0.25rem' }}>Target Personalities</h4>
        <button className="link-btn"
        // onClick={() => onLinkClick("Target Personalities")}
        >{data?.targetedProfile?.map((personality, index) => {
          return personality.personalityName +
            (index !== data?.targetedProfile.length - 1 ? " / " : "")
        })}</button>
        <p style={{ fontSize: '0.8rem', margin: '0.25rem 0 0', color: '#475569' }}>{data?.targetProfileDescription}</p>
      </div>
      <div>
        <h4 style={{ fontSize: '0.7rem', fontWeight: 700, color: 'var(--lywo-primary)', textTransform: 'uppercase', marginBottom: '0.25rem' }}>Candidate Profile</h4>
        <div style={{ fontSize: '1rem' }}>
          <button className="link-btn"
          // onClick={() => onLinkClick(data.profile.primary)}
          >{data?.primaryProfile?.profileName} (Primary)</button>
          <span style={{ margin: '0 0.4rem' }}>/</span>
          <button className="link-btn"
          // onClick={() => onLinkClick(data.profile.secondary)}
          >{data?.secondaryProfile?.profileName} (Secondary)</button>
        </div>
        <p style={{ fontSize: '0.8rem', margin: '0.25rem 0 0', color: '#475569' }}>{data?.primaryProfile?.shortDescriptions} {data?.secondaryProfile?.shortDescriptions}</p>
      </div>
    </div>


    <h4 style={{ fontSize: '0.85rem', fontWeight: 800, color: '#1e293b', marginTop: '1.25rem', marginBottom: '0.5rem' }}>Behavior Mapping</h4>
    <div className="table-responsive">
      <table className="beh-table">
        <thead>
          <tr>
            <th width="15%">Organization Requirement</th>
            <th width="30%">Primary Behavior (Normal)</th>
            <th width="25%">Bridging</th>
            <th width="30%">Secondary Behavior (Fallback)</th>
          </tr>
        </thead>
        <tbody>


          {data?.resumeMatch && data?.resumeMatch[0]?.behaviourFitmentAnalyses?.map((row, i) => (
            <tr key={i}>
              <td className="fw-bold">{row?.trait}</td>
              <td style={{ fontSize: '0.75rem', color: '#475569' }}><div style={{ marginBottom: '0.25rem' }}>{row?.primary}</div></td>
              <td style={{ fontSize: '0.75rem', color: '#475569' }}><span style={{ color: '#475569' }}>{row?.analysis}</span></td>
              <td style={{ fontSize: '0.75rem', color: '#475569' }}><div style={{ marginBottom: '0.25rem' }}>{row?.secondry}</div></td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  </div>
);


const HighlightsSection = ({ data }) => (
  <div className="card highlight-card card-accent-left">
    <div className="d-flex justify-content-between align-items-center mb-3">
      <div className="section-title mb-0" style={{ color: '#1e3a8a' }}><Award size={14} /> Resume Match & Highlights</div>
      <span className="badge match">{data?.resumeMatch && data?.resumeMatch[0]?.match}</span>
    </div>
    <div className="d-grid gap-2">
      {data?.resumeMatch && data?.resumeMatch[0]?.matchDetails?.map((pt, i) => (
        <div key={i} className="highlight-item">
          <Star size={16} color="#f97316" style={{ marginTop: 2, flexShrink: 0 }} />
          <div><strong style={{ color: '#1e3a8a' }}>{pt?.title}:</strong> <span style={{ color: '#475569' }}>{pt?.description}</span></div>
        </div>
      ))}
    </div>
  </div>
);


const CriticalAlerts = ({ data }) => (
  <div className="card card-alert">
    <div className="alert-header">
      <ShieldAlert size={18} /> Critical Areas Requiring Verification
    </div>
    {data?.resumeMatch && data?.resumeMatch[0]?.criticalAreasVerifications?.map((item, i) => (
      <div key={i} className="alert-item"><strong>{item?.title}:</strong> {item?.description}</div>
    ))}
  </div>
);


const WorkHistorySection = ({ data }) => (
  <div className="card">
    <div className="section-title"><Briefcase size={14} /> WORK HISTORY</div>
    <div className="work-grid">
      {data?.resumeMatch && data?.resumeMatch[0]?.workHistoryDetails?.map((job, i) => (
        <div key={i} className="work-item">
          <h4 className="fw-bold" style={{ fontSize: '0.9rem', margin: '0 0 0.2rem' }}>{job?.position}</h4>
          <div className="fw-semibold" style={{ fontSize: '0.8rem', color: '#64748b', marginBottom: '0.5rem' }}>{job?.company}</div>
          <div style={{ fontSize: '0.7rem', background: '#f8f9fa', padding: '2px 8px', border: '1px solid #e2e8f0', borderRadius: '4px', alignSelf: 'start', marginBottom: '0.75rem', fontFamily: 'monospace', color: '#64748b' }}>{job?.duration}</div>
          <div className="work-focus">
            <span className="fw-bold" style={{ display: 'block', fontSize: '0.6rem', color: '#64748b', marginBottom: '3px', textTransform: 'uppercase' }}>FOCUS</span>
            <span style={{ color: '#475569' }}>{job?.focus}</span>
          </div>
        </div>
      ))}
    </div>
  </div>
);


const InfoModal = ({ title, content, onClose }) => {
  if (!content) return null;
  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h3 className="h6 m-0">{title} Profile</h3>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '0.5rem' }}><X size={20} /></button>
        </div>
        {Object.entries(content).map(([k, v]) => (
          <div key={k} className="mb-3">
            <strong style={{ display: 'block', fontSize: '0.7rem', textTransform: 'uppercase', color: 'var(--lywo-primary)', marginBottom: '0.2rem' }}>{k}</strong>
            <p className="m-0" style={{ fontSize: '0.85rem', lineHeight: 1.5, color: '#475569' }}>{v}</p>
          </div>
        ))}
      </div>
    </div>
  );
};


/**
 * =================================================================================
 * 5. MAIN CONTAINER COMPONENT
 * =================================================================================
 */

function Card({ children, className = "", style = {} }) {
  return <div className={`lywo-card ${className}`} style={style}>{children}</div>;
}


const CandidateDashboard = ({ data = DEFAULT_DATA }) => {
  const [modalTitle, setModalTitle] = useState(null);

  const { code, userId } = useParams();

  const { adminData } = useContext(AppContext);

  const [personalitDetails, setPersonalityDetails] = useState({
    isOpen: false,
    per: ""
  });

  const [isLoading, setIsLoading] = useState(false);


  const [candidateData, setCandidateData] = useState({});


  useEffect(() => {
    getSummary()
  }, []);

  const handlePersonality = useCallback((per, isOpen) => {

    setPersonalityDetails({
      isOpen: isOpen,
      per: per
    })

  })


  const getSummary = () => {

    setIsLoading(true);
    simpleGetCallWithErrorResponse(ApiConfig.GET_SUMMARY_REPORT + `UserId=${userId}&Code=${code}&Admin=${adminData?.email}`).then((res) => {

      if (res?.json?.succeded) {
        setCandidateData(res?.json?.response);

        if (!res?.json?.response?.resumeMatch) {
          toastMsg("info", "Resume Match data not found for this candidate.");
        }

      }

      setIsLoading(false);

    })

  }


  return (
    <>
      <PersonalityDetails
        isOpen={personalitDetails?.isOpen}
        per={personalitDetails?.per}
        handleShowPersonality={handlePersonality}
      />
      <div className="mainWrap">
        <style>
          {`@import url('https://cdn.jsdelivr.net/npm/bootstrap@5.3.3/dist/css/bootstrap.min.css');`}
          {CSS_STYLES}
        </style>

        <Header candidate={candidateData} code={code} userId={userId} adminData={adminData} />


        <div className="container mt-3">
          <div className="row g-3">
            {/* Left Sidebar */}
            <div className="col-lg-4">
              <div className="d-flex flex-column gap-3">
                <ProfileCard data={candidateData} />
                <QuestionnaireCard data={candidateData} />
                <NotesSection data={candidateData} />
              </div>
            </div>


            {/* Main Content Area */}
            <div className="col-lg-8">

              <div className="d-flex flex-column gap-3">
                <UserData
                  personalities={candidateData?.personalityGroups || []}
                  roleMapping={candidateData?.resumeMatch ? candidateData?.resumeMatch[0]?.behaviourFitmentAnalyses : []} match={candidateData?.personalityMatch}
                  handleShowPersonality={handlePersonality}
                />
                {/* <BehavioralAnalysis
                data={candidateData}
                onLinkClick={setModalTitle}
              /> */}
                <HighlightsSection data={candidateData} />
                <SkillsSection data={candidateData} />
                <CriticalAlerts data={candidateData} />
              </div>
            </div>
          </div>


          {/* Bottom Section */}
          <div className="row mt-3 mb-4">
            <div className="col-12">
              <WorkHistorySection data={candidateData} />
            </div>
          </div>
        </div>


        <InfoModal
          title={modalTitle}
          content={MODAL_CONTENT[modalTitle]}
          onClose={() => setModalTitle(null)}
        />


        <footer className="text-center text-muted small py-3">
          Generated via Candidate Summary System • {new Date().toLocaleDateString()}
        </footer>

      </div>

    </>
  );
};


export default CandidateDashboard;
